/**
 * Step 5: Validate & Preview
 *
 * Shows validation results in a table with row-level status indicators.
 * Users can toggle rows to skip, and click cells to edit values inline.
 * Edited values are re-validated immediately.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { UploadFieldDef, ValidationResult, RowStatus, LookupData } from '../utils/types';
import { ValidationStats } from '../utils/validate';

interface StepValidatePreviewProps {
	results: ValidationResult[];
	stats: ValidationStats | null;
	fields: UploadFieldDef[];
	lookupData: LookupData;
	onToggleSkip: (rowIndex: number) => void;
	onEditCell?: (rowIndex: number, fieldName: string, newValue: unknown) => void;
}

const STATUS_ICONS: Record<RowStatus, React.ReactNode> = {
	valid: <CheckCircleIcon color="success" fontSize="small" />,
	warning: <WarningIcon color="warning" fontSize="small" />,
	error: <ErrorIcon color="error" fontSize="small" />,
	skipped: <BlockIcon color="disabled" fontSize="small" />,
};

type FilterMode = 'all' | 'errors' | 'valid';

// ---------------------------------------------------------------------------
// EditableCell — inline editor for a single cell
// ---------------------------------------------------------------------------

interface EditableCellProps {
	field: UploadFieldDef;
	value: unknown;
	displayValue?: unknown;
	hasError: boolean;
	hasWarning: boolean;
	errorMessage?: string;
	onCommit: (newValue: unknown) => void;
	disabled: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({
	field,
	value,
	displayValue,
	hasError,
	hasWarning,
	errorMessage,
	onCommit,
	disabled,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	const isRelationResolved =
		field.dataType === 'relation' && typeof value === 'number' && value > 0;

	const displayVal =
		displayValue !== undefined && displayValue !== null
			? String(displayValue).startsWith('__unresolved:')
				? String(displayValue).replace('__unresolved:', '')
				: String(displayValue)
			: '—';

	const startEditing = useCallback(() => {
		if (disabled || isRelationResolved) return;
		setDraft(
			value !== undefined && value !== null
				? String(value).startsWith('__unresolved:')
					? String(value).replace('__unresolved:', '')
					: String(value)
				: ''
		);
		setEditing(true);
		setTimeout(() => inputRef.current?.focus(), 0);
	}, [disabled, isRelationResolved, value]);

	const commitEdit = useCallback(() => {
		setEditing(false);
		const trimmed = draft.trim();
		// Only commit if value actually changed
		if (trimmed !== displayVal && !(trimmed === '' && displayVal === '—')) {
			onCommit(trimmed === '' ? undefined : trimmed);
		}
	}, [draft, displayVal, onCommit]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				commitEdit();
			} else if (e.key === 'Escape') {
				setEditing(false);
			}
		},
		[commitEdit]
	);

	// Enum fields: render dropdown directly
	if (editing && field.dataType === 'enum' && field.enumValues && field.enumValues.length > 0) {
		return (
			<Select
				value={draft}
				onChange={(e) => {
					const newVal = e.target.value;
					setDraft(newVal);
					setEditing(false);
					onCommit(newVal);
				}}
				onClose={() => setEditing(false)}
				size="small"
				autoFocus
				open
				sx={{ minWidth: 100, fontSize: '0.8125rem' }}>
				{!field.required && <MenuItem value="">— None —</MenuItem>}
				{field.enumValues.map((opt) => (
					<MenuItem key={opt} value={opt}>
						{opt}
					</MenuItem>
				))}
			</Select>
		);
	}

	// Boolean fields: toggle on click
	if (field.dataType === 'boolean' && !editing) {
		return (
			<TableCell
				onClick={() => {
					if (disabled) return;
					const current = value === true || value === 'true' || value === 'Yes';
					onCommit(!current);
				}}
				sx={{
					cursor: disabled ? 'default' : 'pointer',
					color: hasError ? 'error.main' : hasWarning ? 'warning.main' : undefined,
					maxWidth: 150,
					'&:hover': disabled ? {} : { bgcolor: 'action.hover' },
				}}>
				{value === true || value === 'true'
					? 'Yes'
					: value === false || value === 'false'
						? 'No'
						: displayVal}
			</TableCell>
		);
	}

	if (editing) {
		return (
			<TextField
				inputRef={inputRef}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commitEdit}
				onKeyDown={handleKeyDown}
				size="small"
				type={field.dataType === 'number' ? 'number' : field.dataType === 'date' ? 'date' : 'text'}
				variant="standard"
				error={hasError}
				helperText={hasError ? errorMessage : undefined}
				sx={{ minWidth: 80, '& input': { fontSize: '0.8125rem', py: 0.25 } }}
				FormHelperTextProps={{ sx: { fontSize: '0.6875rem', mx: 0 } }}
			/>
		);
	}

	return (
		<Tooltip
			title={
				isRelationResolved
					? displayVal
					: hasError
						? `${errorMessage} — click to edit`
						: disabled
							? displayVal
							: 'Click to edit'
			}
			placement="top">
			<Box
				onClick={startEditing}
				sx={{
					cursor: disabled || isRelationResolved ? 'default' : 'pointer',
					color: hasError ? 'error.main' : hasWarning ? 'warning.main' : undefined,
					maxWidth: 150,
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					display: 'flex',
					alignItems: 'center',
					gap: 0.5,
					borderRadius: 0.5,
					px: 0.5,
					py: 0.25,
					'&:hover': disabled || isRelationResolved ? {} : { bgcolor: 'action.hover' },
				}}>
				<span>{displayVal}</span>
				{hasError && !disabled && !isRelationResolved && (
					<EditIcon sx={{ fontSize: 14, opacity: 0.6 }} />
				)}
			</Box>
		</Tooltip>
	);
};

// ---------------------------------------------------------------------------
// StepValidatePreview
// ---------------------------------------------------------------------------

export const StepValidatePreview: React.FC<StepValidatePreviewProps> = ({
	results,
	stats,
	fields,
	lookupData,
	onToggleSkip,
	onEditCell,
}) => {
	const { t } = useTranslation();
	const [filter, setFilter] = useState<FilterMode>('all');

	const relationLabelMaps = useMemo(() => {
		const maps: Record<string, Map<number, string>> = {};
		for (const field of fields) {
			if (field.dataType !== 'relation' || !field.relation) continue;
			const items = lookupData[field.relation.contextKey] ?? [];
			const matchField = field.relation.matchField;
			const labels = new Map<number, string>();
			for (const item of items) {
				const idValue = item[field.relation.idField];
				if (typeof idValue !== 'number') continue;
				labels.set(idValue, String(item[matchField] ?? idValue));
			}
			maps[field.name] = labels;
		}
		return maps;
	}, [fields, lookupData]);

	const filteredResults = useMemo(() => {
		switch (filter) {
			case 'errors':
				return results.filter((r) => r.status === 'error');
			case 'valid':
				return results.filter((r) => r.status === 'valid' || r.status === 'warning');
			default:
				return results;
		}
	}, [results, filter]);

	// Show a limited number of field columns, but always include fields with errors
	const baseFields = fields.slice(0, 6);
	const errorFieldNames = new Set<string>();
	for (const r of results) {
		for (const e of r.errors) {
			if (e.field !== '__cross') errorFieldNames.add(e.field);
		}
	}
	const extraErrorFields = fields.filter(
		(f) => !baseFields.includes(f) && errorFieldNames.has(f.name)
	);
	const displayFields = [...baseFields, ...extraErrorFields];

	if (!stats) return null;

	return (
		<Box>
			{/* Stats summary */}
			<Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
				<Chip
					icon={<CheckCircleIcon />}
					label={t('bulkUpload.validate.valid', { count: stats.valid })}
					color="success"
					variant="outlined"
					size="small"
				/>
				{stats.warning > 0 && (
					<Chip
						icon={<WarningIcon />}
						label={t('bulkUpload.validate.warnings', { count: stats.warning })}
						color="warning"
						variant="outlined"
						size="small"
					/>
				)}
				{stats.error > 0 && (
					<Chip
						icon={<ErrorIcon />}
						label={t('bulkUpload.validate.errors', { count: stats.error })}
						color="error"
						variant="outlined"
						size="small"
					/>
				)}
				{stats.skipped > 0 && (
					<Chip
						icon={<BlockIcon />}
						label={t('bulkUpload.validate.skipped', { count: stats.skipped })}
						variant="outlined"
						size="small"
					/>
				)}
			</Box>

			{stats.error > 0 && (
				<Alert severity="info" sx={{ mb: 2 }}>
					{onEditCell
						? t('bulkUpload.validate.errorRowsEditable', {
								count: stats.error,
								defaultValue:
									'{{count}} row(s) have errors. Click any cell to edit values inline, or skip rows to exclude them.',
							})
						: t('bulkUpload.validate.errorRowsNote', { count: stats.error })}
				</Alert>
			)}

			{/* Filter toggle */}
			<Box sx={{ mb: 2 }}>
				<ToggleButtonGroup
					value={filter}
					exclusive
					onChange={(_, val) => val && setFilter(val)}
					size="small">
					<ToggleButton value="all">
						{t('bulkUpload.validate.filterAll', { count: stats.total })}
					</ToggleButton>
					<ToggleButton value="errors">
						{t('bulkUpload.validate.filterErrors', { count: stats.error })}
					</ToggleButton>
					<ToggleButton value="valid">
						{t('bulkUpload.validate.filterValid', { count: stats.valid + stats.warning })}
					</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{/* Results table */}
			<TableContainer sx={{ maxHeight: 350 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 50, fontWeight: 'bold' }}>#</TableCell>
							<TableCell sx={{ width: 50, fontWeight: 'bold' }}>{t('status')}</TableCell>
							{displayFields.map((f) => (
								<TableCell key={f.name} sx={{ fontWeight: 'bold' }}>
									{f.label}
								</TableCell>
							))}
							<TableCell sx={{ width: 100, fontWeight: 'bold' }}>
								{t('bulkUpload.validate.issues')}
							</TableCell>
							<TableCell sx={{ width: 50 }} />
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredResults.map((result) => {
							const isSkipped = result.status === 'skipped';
							return (
								<TableRow
									key={result.rowIndex}
									sx={{
										opacity: isSkipped ? 0.5 : 1,
										bgcolor: result.status === 'error' ? 'rgba(255, 0, 0, 0.04)' : undefined,
									}}>
									<TableCell>{result.rowIndex + 1}</TableCell>
									<TableCell>{STATUS_ICONS[result.status]}</TableCell>
									{displayFields.map((f) => {
										const val = result.resolvedRow[f.name];
										const relationDisplayVal =
											f.dataType === 'relation' && typeof val === 'number'
												? relationLabelMaps[f.name]?.get(val) ?? val
												: val;
										const fieldError = result.errors.find((e) => e.field === f.name);
										const hasWarning = result.warnings.some((w) => w.field === f.name);

										// Boolean cells handle their own TableCell
										if (f.dataType === 'boolean') {
											return (
												<EditableCell
													key={f.name}
													field={f}
													value={val}
													displayValue={relationDisplayVal}
													hasError={!!fieldError}
													hasWarning={hasWarning}
													errorMessage={fieldError?.message}
													onCommit={(newVal) => onEditCell?.(result.rowIndex, f.name, newVal)}
													disabled={isSkipped || !onEditCell}
												/>
											);
										}

										return (
											<TableCell key={f.name} sx={{ p: 0.5 }}>
												<EditableCell
													field={f}
													value={val}
													displayValue={relationDisplayVal}
													hasError={!!fieldError}
													hasWarning={hasWarning}
													errorMessage={fieldError?.message}
													onCommit={(newVal) => onEditCell?.(result.rowIndex, f.name, newVal)}
													disabled={isSkipped || !onEditCell}
												/>
											</TableCell>
										);
									})}
									<TableCell>
										{result.errors.length > 0 && (
											<Tooltip title={result.errors.map((e) => e.message).join('\n')}>
												<Chip label={result.errors.length} color="error" size="small" />
											</Tooltip>
										)}
										{result.warnings.length > 0 && (
											<Tooltip title={result.warnings.map((w) => w.message).join('\n')}>
												<Chip
													label={result.warnings.length}
													color="warning"
													size="small"
													sx={{ ml: 0.5 }}
												/>
											</Tooltip>
										)}
									</TableCell>
									<TableCell>
										<Tooltip
											title={
												isSkipped
													? t('bulkUpload.validate.includeRow')
													: t('bulkUpload.validate.skipRow')
											}>
											<IconButton size="small" onClick={() => onToggleSkip(result.rowIndex)}>
												{isSkipped ? (
													<VisibilityIcon fontSize="small" />
												) : (
													<VisibilityOffIcon fontSize="small" />
												)}
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			{filteredResults.length === 0 && (
				<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
					{t('bulkUpload.validate.noRowsMatch')}
				</Typography>
			)}
		</Box>
	);
};
